/**
 * Deferred-branching visitedPath update.
 *
 * NOTE: This is a copy of the backend function used as a frontend lookahead.
 * Keep both in sync: packages/acquire-api/src/services/OnboardingApplicationService/computeNextVisitedPath.ts
 *
 * Rules:
 *  - currentSlug not in visitedPath:
 *      - If prev was mid-path: branching → truncate after prev, then append.
 *      - Else: append.
 *  - prev is before currentSlug with a gap: forward skip → remove skipped
 *    steps (user changed a conditional and the branch rejoined downstream).
 *    (Checked first — applies even when currentSlug is the tail element.)
 *  - currentSlug == last element: no-op (edit of tail).
 *  - prev is directly before currentSlug: forward nav → no change.
 *  - Otherwise (backward/non-linear jump): defer — return unchanged.
 *    Next submit will reveal intent.
 */
export default function computeNextVisitedPath(
  visitedPath: string[] | undefined,
  currentSlug: string,
  lastVisitedSlug: string | null | undefined
): string[] {
  if (!visitedPath || visitedPath.length === 0) return [currentSlug];

  const xIdx = visitedPath.indexOf(currentSlug);
  const prevIdx = lastVisitedSlug ? visitedPath.indexOf(lastVisitedSlug) : -1;
  const lastIdx = visitedPath.length - 1;

  if (xIdx === -1) {
    // currentSlug is new to the path.
    if (prevIdx !== -1 && prevIdx < lastIdx) {
      // User jumped mid-path (back button, admin edit) and is now submitting
      // a step NOT in the existing path — branching. Truncate then append.
      return [...visitedPath.slice(0, prevIdx + 1), currentSlug];
    }
    return [...visitedPath, currentSlug];
  }

  if (prevIdx >= 0 && prevIdx < xIdx - 1) {
    // Forward navigation that skipped steps between prev and current.
    // The user changed a conditional answer and the new branch rejoined
    // the existing path at currentSlug, bypassing intermediate steps.
    // Remove the skipped steps — they belong to an abandoned branch.
    // NOTE: Must run before the tail-edit check — the rejoin point may be
    // the last element of visitedPath, and we still need to prune skipped steps.
    return [...visitedPath.slice(0, prevIdx + 1), ...visitedPath.slice(xIdx)];
  }

  if (xIdx === lastIdx) return visitedPath; // tail edit
  if (prevIdx === xIdx - 1) return visitedPath; // natural forward nav

  return visitedPath; // deferred — wait for next submit to reveal intent
}
